import { useCallback, useEffect, useRef, useState } from 'react'

import { DEFAULT_TIMEOUT, EXIT_DURATION_MS, REVEAL_GAP_MS } from './constants'
import { Toast, ToastOptions, VisibleToast } from './types'

const defaultOptions = {
  timeout: DEFAULT_TIMEOUT,
  sticky: false
}

let nextToastId = 0

/**
 * Two toasts are "the same message" when they share a groupId, or — with no
 * groupId — when their text is identical. The groupId case is what lets a
 * status line whose wording changes stay on one card.
 */
const isSameMessage = (a: Toast, b: Toast) =>
  a.groupId || b.groupId ? a.groupId === b.groupId : a.text === b.text

const timerKey = (toast: VisibleToast) => `${toast.id}:${toast.revision}:${toast.phase}`

/**
 * Paced toast queue.
 *
 * Invariant: the pending queue drains at one reveal per REVEAL_GAP_MS, and the
 * visible set is a sliding window over the last `maxVisible` revealed toasts.
 * Being at capacity never blocks a reveal — it evicts the oldest instead, so
 * what's on screen is always the most recent N.
 *
 * `liveRef` — not the `visible` state — is the authoritative list. addToast is
 * called synchronously in bursts (a controller emitting five errors in one
 * tick), and a dedupe check against state would read a list React hasn't
 * committed yet and let duplicates through. State is a published copy.
 */
const useToastQueue = (maxVisible: number) => {
  const [visible, setVisible] = useState<VisibleToast[]>([])

  const liveRef = useRef<VisibleToast[]>([])
  const pendingRef = useRef<Toast[]>([])
  const lastRevealAtRef = useRef(0)
  const pumpTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const timersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>())
  const maxVisibleRef = useRef(maxVisible)
  const isMountedRef = useRef(false)

  const publish = useCallback(() => {
    setVisible(liveRef.current)
  }, [])

  /** Flips the oldest 'in' toasts to 'out' until the list fits the cap. */
  const applyCap = useCallback((toasts: VisibleToast[]): VisibleToast[] => {
    const live = toasts.filter((t) => t.phase === 'in')
    const overflow = live.length - maxVisibleRef.current

    if (overflow <= 0) return toasts

    const evicted = new Set(live.slice(0, overflow).map((t) => t.id))

    return toasts.map((t) => (evicted.has(t.id) ? { ...t, phase: 'out' } : t))
  }, [])

  /**
   * Reveals at most one toast, then re-arms itself while work remains.
   *
   * This is a ref-driven pump rather than an effect scheduler for three
   * reasons: addToast must keep a stable identity (it sits in several useEffect
   * dependency arrays that subscribe to the background/worker toast bridges);
   * the pacing rule is a fact about wall-clock time, not about props, and an
   * effect depending on `lastRevealedAt` would re-trigger itself; and clearing
   * its own timer up front makes pump idempotent, so it can be called from
   * anywhere without coordination.
   */
  const pump = useCallback(() => {
    if (pumpTimerRef.current) {
      clearTimeout(pumpTimerRef.current)
      pumpTimerRef.current = null
    }

    if (!isMountedRef.current || !pendingRef.current.length) return

    const now = Date.now()
    const waited = now - lastRevealAtRef.current

    if (waited < REVEAL_GAP_MS) {
      pumpTimerRef.current = setTimeout(pump, REVEAL_GAP_MS - waited)

      return
    }

    const next = pendingRef.current.shift()
    if (!next) return

    lastRevealAtRef.current = now
    liveRef.current = applyCap([...liveRef.current, { ...next, phase: 'in', revision: 0 }])
    publish()

    if (pendingRef.current.length) {
      pumpTimerRef.current = setTimeout(pump, REVEAL_GAP_MS)
    }
  }, [applyCap, publish])

  const hardRemove = useCallback(
    (tId: number) => {
      liveRef.current = liveRef.current.filter((t) => t.id !== tId)
      publish()
    },
    [publish]
  )

  /** Starts the exit; the slot is freed immediately. */
  const dismiss = useCallback(
    (tId: number) => {
      liveRef.current = liveRef.current.map((t) => (t.id === tId ? { ...t, phase: 'out' } : t))
      publish()
    },
    [publish]
  )

  const addToast = useCallback(
    (text: string, options?: ToastOptions) => {
      const toast: Toast = {
        id: nextToastId++,
        text,
        ...defaultOptions,
        ...(options || {})
      }

      // Make sure that toasts won't be duplicated.
      // Still pending: replace in place rather than re-queueing at the tail, so
      // a controller re-emitting the same message can't push itself behind
      // newer ones forever.
      const pendingIndex = pendingRef.current.findIndex((t) => isSameMessage(t, toast))
      if (pendingIndex !== -1) {
        pendingRef.current[pendingIndex] = toast

        return toast.id
      }

      // Already on screen: update that card in place. Keeping the existing `id`
      // is what stops React from remounting it — a remount replays the enter
      // animation and reads as a flicker. Bumping `revision` restarts the
      // countdown; an already-exiting card is revived rather than duplicated.
      const existing = liveRef.current.find((t) => isSameMessage(t, toast))
      if (existing) {
        liveRef.current = applyCap(
          liveRef.current.map((t) =>
            t.id === existing.id
              ? { ...toast, id: existing.id, phase: 'in', revision: t.revision + 1 }
              : t
          )
        )
        publish()

        return existing.id
      }

      pendingRef.current.push(toast)
      pump()

      return toast.id
    },
    [applyCap, publish, pump]
  )

  useEffect(() => {
    const timers = timersRef.current

    isMountedRef.current = true
    // Refs survive a remount while state doesn't, so resume any pending work.
    pump()

    return () => {
      isMountedRef.current = false

      if (pumpTimerRef.current) clearTimeout(pumpTimerRef.current)
      pumpTimerRef.current = null

      timers.forEach(clearTimeout)
      timers.clear()
    }
  }, [pump])

  // Reconcile timers against the visible set instead of arming and disarming
  // per render — a cleanup function here would restart every countdown on any
  // unrelated re-render. Keying on id+revision+phase means an in-place update
  // (or a dismiss) retires the old timer and arms a fresh one.
  useEffect(() => {
    const timers = timersRef.current
    const liveKeys = new Set(visible.map(timerKey))

    timers.forEach((handle, key) => {
      if (liveKeys.has(key)) return

      clearTimeout(handle)
      timers.delete(key)
    })

    visible.forEach((toast) => {
      const key = timerKey(toast)
      if (timers.has(key)) return

      if (toast.phase === 'out') {
        // Unconditional, never an animation callback — a slot can't get stuck.
        timers.set(
          key,
          setTimeout(() => hardRemove(toast.id), EXIT_DURATION_MS)
        )

        return
      }

      if (toast.sticky) return

      timers.set(
        key,
        setTimeout(() => dismiss(toast.id), toast.timeout ?? DEFAULT_TIMEOUT)
      )
    })
  }, [visible, dismiss, hardRemove])

  // Resizing between surfaces can shrink the cap; trim the oldest to fit.
  useEffect(() => {
    maxVisibleRef.current = maxVisible

    const capped = applyCap(liveRef.current)
    if (capped !== liveRef.current) {
      liveRef.current = capped
      publish()
    }

    pump()
  }, [maxVisible, applyCap, publish, pump])

  return { visible, addToast, dismiss }
}

export default useToastQueue
